import { fmtPrice } from "../lib/format";
import { Rating, VerifiedBadge } from "../components/Rating";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n, catName, tr } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Star, MapPin, Clock, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";
import { SITE_URL, OG_IMAGE } from "../lib/seo";

export default function ServiceDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const [svc, setSvc] = useState(null);
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);

  useEffect(() => { api.get(`/services/${id}`).then((r) => setSvc(r.data)); }, [id]);

  const title = svc ? `${svc.title} | Refine Touch USA` : "Refine Touch USA";
  const description = svc ? (svc.description || "").slice(0, 160) : undefined;
  const canonical = svc ? `${SITE_URL}/services/${svc.id}` : undefined;
  const jsonLd = svc ? [{
    "@context": "https://schema.org",
    "@type": "Service",
    name: svc.title,
    description: svc.description,
    areaServed: svc.location,
    offers: { "@type": "Offer", price: svc.price, priceCurrency: "USD" },
    provider: svc.provider ? { "@type": "ProfessionalService", name: svc.provider.name, address: svc.provider.location } : undefined,
  }, {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
      { "@type": "ListItem", position: 3, name: svc.title, item: canonical },
    ],
  }] : [];
  useSEO({ title, description, canonical, ogImage: svc?.images?.[0] || OG_IMAGE, jsonLd });

  if (!svc) return <div className="max-w-7xl mx-auto p-10">{t("loading")}</div>;

  const handleBook = async () => {
    if (!user) return nav("/login?next=" + encodeURIComponent(`/services/${id}`));
    setBooking(true);
    try {
      const scheduled = new Date(date);
      const [h, m] = time.split(":");
      scheduled.setHours(parseInt(h), parseInt(m), 0, 0);
      const { data: b } = await api.post("/bookings", {
        service_id: id, scheduled_at: scheduled.toISOString(), notes,
      });
      // Trigger checkout (mock)
      const { data: co } = await api.post("/payments/checkout", {
        booking_id: b.id, origin_url: window.location.origin,
      });
      window.location.href = co.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("err_booking"));
      setBooking(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <Link to="/services" className="text-sm text-stone-600 flex items-center gap-1"><ArrowLeft size={14} /> {t("back")}</Link>
      <div className="grid lg:grid-cols-3 gap-10 mt-4">
        <div className="lg:col-span-2">
          <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-stone-100">
            <img src={svc.images?.[0]} className="w-full h-full object-cover" alt="" />
          </div>
          <div className="mt-6">
            <div className="eyebrow text-stone-500">{catName(svc.category, lang)}</div>
            <h1 className="font-serif text-5xl leading-tight mt-2">{tr(svc, "title", lang)}</h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-stone-600">
              <span className="flex items-center gap-1"><MapPin size={14} />{svc.location}</span>
              <span className="flex items-center gap-1"><Clock size={14} />{svc.duration_minutes} {t("duration_min")}</span>
              <Rating rating={svc.rating} count={svc.review_count} />
            </div>
          </div>
          <div className="mt-8 prose prose-stone max-w-none">
            <p className="text-stone-700 leading-relaxed">{tr(svc, "description", lang)}</p>
          </div>

          <div className="mt-10 bg-white border border-stone-200 rounded-2xl p-6">
            <div className="eyebrow text-stone-500 mb-2">{t("provider")}</div>
            <Link to={`/providers/${svc.provider?.id}`} className="flex items-center gap-4">
              <img src={svc.provider?.avatar} className="w-14 h-14 rounded-full object-cover" alt="" />
              <div>
                <div className="font-serif text-xl">{svc.provider?.name}</div>
                <div className="text-sm text-stone-500">{svc.provider?.location} · {svc.provider?.years_experience} {t("years_exp")}</div>
              </div>
            </Link>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
            <div className="flex items-baseline justify-between">
              <div className="font-serif text-4xl">{fmtPrice(svc.price)}</div>
              <div className="text-xs text-stone-500">{t("per_service")}</div>
            </div>
            <div className="mt-6">
              <div className="text-sm font-medium mb-2">{t("pick_date")}</div>
              <div className="border border-stone-200 rounded-xl p-2">
                <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
              </div>
              <div className="text-sm font-medium mt-4 mb-2">{t("time")}</div>
              <div className="grid grid-cols-4 gap-2">
                {["09:00","10:00","11:00","14:00","15:00","16:00","17:00","18:00"].map((h) => (
                  <button key={h} data-testid={`time-${h}`} onClick={() => setTime(h)} className={`text-sm py-2 rounded-lg border ${time === h ? "bg-stone-900 text-stone-50 border-stone-900" : "border-stone-200 hover:bg-stone-100"}`}>{h}</button>
                ))}
              </div>
              <Textarea data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("notes_opt")} className="mt-4" />
              <Button data-testid="book-now-btn" disabled={booking} onClick={handleBook} className="w-full mt-4 rounded-xl h-12 bg-stone-900 hover:bg-stone-800 text-stone-50 btn-pill">
                {booking ? t("booking_in_progress") : `${t("book_now")} · ${fmtPrice(svc.price)}`}
              </Button>
              <div className="flex items-center gap-2 mt-4 text-xs text-stone-500">
                <ShieldCheck size={14} /> {t("secure_pay")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
